import { dirOf, isShipped } from '../i18n/locale.ts';
import { t, type PlainKey } from '../i18n/t.ts';
import { patternLabel } from './practice.ts';

/**
 * Kept dependency-free from Relay's generated types (bare `status: string`
 * rather than the generated enum) so this module — the testable seam,
 * following `topics.ts` — stays importable from a plain `node --test` run,
 * the same reason `DebriefView.tsx` cannot be: `.tsx` has no loader here.
 */
export type DebriefViewState = 'waiting' | 'failed' | 'ready' | 'none' | 'skipped';

/**
 * `hasTurns` separates the two ways a session can have no debrief.
 *
 * `none` is a debrief that is coming but not yet here — the gap between
 * `endSession` returning and its enqueue publish arriving — so the screen
 * waits. `skipped` is a session that recorded nothing: `endSession`
 * deliberately queues no debrief for one (see `Session::has_learner_turns`),
 * so waiting for it is waiting forever. `DebriefView` decides `hasTurns`
 * through `debriefExpected`.
 */
export function debriefState(debrief: { status: string } | null | undefined, hasTurns = true): DebriefViewState {
  if (!debrief) return hasTurns ? 'none' : 'skipped';
  switch (debrief.status) {
    case 'PENDING':
    case 'RUNNING':
      return 'waiting';
    case 'FAILED':
      return 'failed';
    case 'DONE':
      return 'ready';
    default:
      return 'none';
  }
}

/**
 * Whether a session will never have a debrief, read off the session alone.
 *
 * Both enqueue paths are atomic with ending the session: `endSession` shares
 * its transaction, `sweep_idle` is one statement. So a session read as ended
 * with no debrief row has none coming, and the caller that has no turn count
 * — history — can still tell `skipped` from `none`.
 */
export function noDebriefComing(session: { status: string; debrief: unknown } | null | undefined): boolean {
  return !!session && session.status !== 'ACTIVE' && session.debrief == null;
}

/**
 * Whether a debrief is coming, for `debriefState`'s `hasTurns`.
 *
 * A caller that counted the learner's turns is trusted: the live screen
 * knows, and the store it reads can hold `status: ENDED` from `endSession`'s
 * response beside the null debrief link it mounted with — a pair the server
 * never sent, which `noDebriefComing` would read as "too short". Only a
 * caller that does not know — history, reading a fresh response — asks the
 * session.
 */
export function debriefExpected(
  hasTurns: boolean | undefined,
  session: { status: string; debrief: unknown } | null | undefined,
): boolean {
  return hasTurns ?? !noDebriefComing(session);
}

/** One `Session.practice` entry, as the debrief query selects it. */
export type PractisedInput = {
  item: {
    id: string;
    pattern: string;
    exemplar: { learnerForm: string; targetForm: string } | null | undefined;
  };
  outcome: string;
};

export type PractisedRow = {
  id: string;
  label: string;
  learnerForm: string | null;
  targetForm: string | null;
  outcome: string;
};

/**
 * The outcome as the debrief words it.
 *
 * Not the progress screen's `STATUS_LABEL`: once the worker has run, a
 * `TARGETED` row is one the conversation never exercised, and "Practised"
 * would say it was.
 */
const OUTCOME_LABEL: Partial<Record<string, PlainKey>> = {
  USED_CORRECTLY: 'debrief-outcome-used-correctly',
  RECURRED: 'debrief-outcome-recurred',
  TARGETED: 'debrief-outcome-targeted',
};

/** `Session.practisedWord`, as the debrief query selects it. */
export type PractisedWordInput = { item: { id: string; word: string }; outcome: string };

/**
 * A word's outcome in its own copy (word-practice §7). Not the patterns'
 * labels: "used" is a claim about production, which is all a string match
 * knows, and not about correctness in every sense.
 */
const WORD_OUTCOME_LABEL: Partial<Record<string, PlainKey>> = {
  USED_CORRECTLY: 'debrief-word-used',
  RECURRED: 'debrief-word-recurred',
  TARGETED: 'debrief-word-targeted',
};

/** A label from one of the tables above, or the wire value when the table has none. */
function labelled(table: Partial<Record<string, PlainKey>>, value: string): string {
  const key = table[value];
  return key ? t(key) : value;
}

/**
 * Targeted-practice §9's block: each pattern the session was steered at, in
 * plain language, in the learner's own earlier words, and how it went. In the
 * server's order — it is the session's own record, not a ranking.
 *
 * A practised word leads, in quotes and with no exemplar line: it is the
 * target the learner chose by hand (word-practice §7).
 */
export function practisedRows(
  practice: readonly PractisedInput[] | null | undefined,
  word?: PractisedWordInput | null,
): PractisedRow[] {
  const patterns = (practice ?? []).map(({ item, outcome }) => ({
    id: item.id,
    label: patternLabel(item.pattern),
    learnerForm: item.exemplar?.learnerForm ?? null,
    targetForm: item.exemplar?.targetForm ?? null,
    outcome: labelled(OUTCOME_LABEL, outcome),
  }));
  if (!word) return patterns;
  return [
    {
      id: word.item.id,
      label: t('common-quoted', { text: word.item.word }),
      learnerForm: null,
      targetForm: null,
      outcome: labelled(WORD_OUTCOME_LABEL, word.outcome),
    },
    ...patterns,
  ];
}

/** One `Turn.errors` entry, as the debrief query selects it. */
export type CitedErrorInput = {
  id: string;
  severity: string;
  learnerForm: string;
  targetForm: string;
  explanation: string;
  span: { start: number } | null | undefined;
};

export type CitedError = {
  id: string;
  learnerForm: string;
  targetForm: string;
  explanation: string | null;
  severity: string;
};

/** Parent design §10's definitions, the same ones the extraction prompt is given. */
const SEVERITY_LABEL: Partial<Record<string, PlainKey>> = {
  BLOCKING: 'severity-blocking',
  MINOR: 'severity-minor',
  POLISH: 'severity-polish',
};

const SEVERITY_RANK: Record<string, number> = { BLOCKING: 0, MINOR: 1, POLISH: 2 };

export function severityLabel(severity: string): string {
  return labelled(SEVERITY_LABEL, severity);
}

/**
 * Cited-errors §3: what got in the way first, then reading order. Sorted here
 * because the server's order is by encrypted id and means nothing; sorting the
 * loader instead would reorder every other `Turn.errors` reader for one view.
 */
export function citedErrors(errors: readonly CitedErrorInput[]): CitedError[] {
  const rank = (e: CitedErrorInput) => SEVERITY_RANK[e.severity] ?? Object.keys(SEVERITY_RANK).length;
  const at = (e: CitedErrorInput) => e.span?.start ?? Number.MAX_SAFE_INTEGER;
  return [...errors]
    .sort((a, b) => rank(a) - rank(b) || at(a) - at(b))
    .map(({ id, learnerForm, targetForm, explanation, severity }) => ({
      id,
      learnerForm,
      targetForm,
      explanation: explanation.trim() || null,
      severity,
    }));
}

/**
 * The `lang` and `dir` for a debrief's summary, strengths and `why`
 * (native-language UI spec §6.2). English when the server wrote none, which
 * is every debrief before phase 3a. `auto` for a code with no catalog here,
 * since this client cannot know its direction and the text can.
 */
export function generatedTextAttrs(language: string | null | undefined): {
  lang: string;
  dir: 'ltr' | 'rtl' | 'auto';
} {
  if (!language) return { lang: 'en', dir: 'ltr' };
  return { lang: language, dir: isShipped(language) ? dirOf(language) : 'auto' };
}
